import { inject, Injectable, signal } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { catchError, Observable, throwError } from 'rxjs';
import {
  Agenda,
  AgendaEvent,
  CreateAgendaDto,
  CreateAgendaEventDto,
  GetAgendaEventsParams,
  GetEventsForReportsParams,
  GetEventsForReportsResponse,
  Professional,
  UpdateAgendaDto,
  UpdateAgendaEventDto,
} from '../../shared/interfaces';
import { REQUIRE_AUTH } from '../../shared/interceptors/auth-context';
import { API_BASE_URL } from '../../shared/api/api.tokens';

@Injectable({ providedIn: 'root' })
export class AgendaService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  /** Incremented each time an event is created from outside the calendar (e.g. header menu). */
  private _eventCreatedTick = signal(0);
  readonly eventCreatedTick = this._eventCreatedTick.asReadonly();

  /** Notify listeners (calendar) that an event was created externally. */
  notifyEventCreated(): void {
    this._eventCreatedTick.update((v) => v + 1);
  }

  /**
   * GET /api/v1/agendas?businessId=...&professionalId=?
   */
  getAgendas(
    businessId: string,
    professionalId?: string
  ): Observable<Agenda[]> {
    let params = new HttpParams().set('businessId', businessId);
    if (professionalId) params = params.set('professionalId', professionalId);

    return this.http
      .get<Agenda[]>(`${this.baseUrl}/api/v1/agendas`, {
        params,
        context: new HttpContext().set(REQUIRE_AUTH, true),
      })
      .pipe(catchError(this.handleHttpError));
  }

  /**
   * GET /api/v1/agendas/{agendaId}
   */
  getAgenda(agendaId: string): Observable<Agenda> {
    return this.http
      .get<Agenda>(`${this.baseUrl}/api/v1/agendas/${agendaId}`, {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      })
      .pipe(catchError(this.handleHttpError));
  }

  /**
   * POST /api/v1/agendas
   */
  createAgenda(payload: CreateAgendaDto): Observable<Agenda> {
    return this.http
      .post<Agenda>(`${this.baseUrl}/api/v1/agendas`, payload, {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      })
      .pipe(catchError(this.handleHttpError));
  }

  /**
   * Update /api/v1/agendas
   */
  updateAgenda(agendaId: string, payload: UpdateAgendaDto) {
    return this.http
      .put<Agenda>(`${this.baseUrl}/api/v1/agendas/${agendaId}`, payload, {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      })
      .pipe(catchError(this.handleHttpError));
  }

  /**
   * DELETE /api/v1/agendas/{agendaId}
   */
  deleteAgenda(agendaId: string) {
    return this.http
      .delete(`${this.baseUrl}/api/v1/agendas/${agendaId}`, {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      })
      .pipe(catchError(this.handleHttpError));
  }

  /**
   * POST /api/v1/events
   */
  createAgendaEvent(payload: CreateAgendaEventDto): Observable<AgendaEvent> {
    const body: any = {
      ...payload,
      startDate: this.toIso(payload.startDate),
      endDate: this.toIso(payload.endDate),
    };

    // Handle recurrence settings if provided
    if (payload.recurrence) {
      body.recurrence = {
        frequency: payload.recurrence.frequency,
        daysOfWeek: payload.recurrence.daysOfWeek || null,
        endDate: payload.recurrence.endDate ? this.toIso(payload.recurrence.endDate) : null,
        occurrenceCount: payload.recurrence.occurrenceCount || null,
      };
    }

    return this.http.post<AgendaEvent>(
      `${this.baseUrl}/api/v1/events`,
      body,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  /**
   * GET /api/v1/businesses/{businessId}/services/{serviceId}/professionals
   * Obtiene los profesionales relacionados a un servicio
   */
  getProfessionalsByService(businessId: string, serviceId: string): Observable<Professional[]> {
    return this.http.get<Professional[]>(
      `${this.baseUrl}/api/v1/businesses/${businessId}/services/${serviceId}/professionals`,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  /**
   * GET /api/v1/events
   * Recibe como filtros: professionalId, clientId, startDate, endDate, statusCode, businessId, origin
   */
  getEvents(params?: {
    professionalId?: string;
    clientId?: string;
    startDate?: string;
    endDate?: string;
    statusCode?: string;
    businessId?: string;
    origin?: string;
  }): Observable<AgendaEvent[]> {
    let httpParams = new HttpParams();
    if (params?.professionalId) httpParams = httpParams.set('professionalId', params.professionalId);
    if (params?.clientId) httpParams = httpParams.set('clientId', params.clientId);
    if (params?.startDate) httpParams = httpParams.set('startDate', params.startDate);
    if (params?.endDate) httpParams = httpParams.set('endDate', params.endDate);
    if (params?.statusCode) httpParams = httpParams.set('statusCode', params.statusCode);
    if (params?.businessId) httpParams = httpParams.set('businessId', params.businessId);
    if (params?.origin) httpParams = httpParams.set('origin', params.origin);

    return this.http.get<AgendaEvent[]>(`${this.baseUrl}/api/v1/events`, {
      params: httpParams,
      context: new HttpContext().set(REQUIRE_AUTH, true),
    }).pipe(catchError(this.handleHttpError));
  }

  /**
   * GET /api/v1/events/external
   * Obtiene eventos que no fueron creados desde el calendario interno (Website, AiAgent, etc.)
   */
  getExternalEvents(params: {
    businessId: string;
    startDate?: string | Date;
    endDate?: string | Date;
    origin?: string;
  }): Observable<AgendaEvent[]> {
    let httpParams = new HttpParams().set('businessId', params.businessId);

    if (params.startDate) {
      const startIso = params.startDate instanceof Date
        ? params.startDate.toISOString()
        : this.toIso(params.startDate);
      if (startIso) httpParams = httpParams.set('startDate', startIso);
    }

    if (params.endDate) {
      const endIso = params.endDate instanceof Date
        ? params.endDate.toISOString()
        : this.toIso(params.endDate);
      if (endIso) httpParams = httpParams.set('endDate', endIso);
    }

    if (params.origin) {
      httpParams = httpParams.set('origin', params.origin);
    }

    return this.http.get<AgendaEvent[]>(`${this.baseUrl}/api/v1/events/external`, {
      params: httpParams,
      context: new HttpContext().set(REQUIRE_AUTH, true),
    }).pipe(catchError(this.handleHttpError));
  }

  /**
   * GET /api/v1/events (legacy method)
   * Recibe como filtros: professionalId, clientId, startDate, endDate, statusCode
   */
  getAgendaEvents(
    filters: GetAgendaEventsParams = {}
  ): Observable<AgendaEvent[]> {
    let params = new HttpParams();

    const add = (k: string, v: unknown) => {
      if (v === undefined || v === null || v === '') return;
      if (v instanceof Date) {
        params = params.set(k, v.toISOString());
      } else if (k === 'startDate' || k === 'endDate') {
        // también normaliza string → ISO si es posible
        const iso = this.toIso(v as string | Date);
        if (iso) params = params.set(k, iso);
      } else {
        params = params.set(k, String(v));
      }
    };

    // Map agendaId to professionalId for backward compatibility
    if (filters.agendaId) {
      add('professionalId', filters.agendaId);
    }
    add('professionalId', filters.professionalId);
    add('clientId', filters.clientId);
    add('startDate', filters.startDate);
    add('endDate', filters.endDate);
    add('statusCode', filters.statusCode);
    add('businessId', filters.businessId);

    return this.http
      .get<AgendaEvent[]>(`${this.baseUrl}/api/v1/events`, {
        params,
        context: new HttpContext().set(REQUIRE_AUTH, true),
      })
      .pipe(catchError(this.handleHttpError));
  }

  /**
   * GET /api/v1/events/{eventId}
   */
  getEventById(eventId: string): Observable<AgendaEvent> {
    return this.http
      .get<AgendaEvent>(`${this.baseUrl}/api/v1/events/${eventId}`, {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      })
      .pipe(catchError(this.handleHttpError));
  }

  /**
   * PUT /api/v1/events/{eventId}
   * Actualiza un evento (normaliza start/end a ISO si vienen como Date o string local)
   */
  updateAgendaEvent(eventId: string, dto: UpdateAgendaEventDto) {
    const body: any = {
      ...dto,
      ...(dto.startDate !== undefined
        ? { startDate: this.toIso(dto.startDate) }
        : {}),
      ...(dto.endDate !== undefined
        ? { endDate: this.toIso(dto.endDate) }
        : {}),
    };

    return this.http
      .put<AgendaEvent>(
        `${this.baseUrl}/api/v1/events/${eventId}`,
        body,
        {
          context: new HttpContext().set(REQUIRE_AUTH, true),
        }
      )
      .pipe(catchError(this.handleHttpError));
  }

  /**
   * DELETE /api/v1/events/{eventId}
   * Elimina un evento por ID
   */
  deleteAgendaEvent(eventId: string): Observable<{ message: string }> {
    return this.http
      .delete<{ message: string }>(
        `${this.baseUrl}/api/v1/events/${eventId}`,
        { context: new HttpContext().set(REQUIRE_AUTH, true) }
      )
      .pipe(catchError(this.handleHttpError));
  }

  private handleHttpError(err: HttpErrorResponse) {
    // Don't convert to Error - keep the HttpErrorResponse so the component can access err.error
    return throwError(() => err);
  }

  /**
   * GET /api/v1/reports/events
   * Obtiene eventos para reportes con paginación y filtros avanzados
   */
  getEventsForReports(
    params: GetEventsForReportsParams = {}
  ): Observable<GetEventsForReportsResponse> {
    let httpParams = new HttpParams();

    const add = (k: string, v: unknown) => {
      if (v === undefined || v === null || v === '') return;
      if (v instanceof Date) {
        httpParams = httpParams.set(k, v.toISOString());
      } else if (k === 'startDate' || k === 'endDate') {
        const iso = this.toIso(v as string | Date);
        if (iso) httpParams = httpParams.set(k, iso);
      } else {
        httpParams = httpParams.set(k, String(v));
      }
    };

    add('agendaId', params.agendaId);
    add('clientId', params.clientId);
    add('startDate', params.startDate);
    add('endDate', params.endDate);
    add('statusCode', params.statusCode);
    add('businessId', params.businessId);
    if (params.page !== undefined)
      httpParams = httpParams.set('page', params.page.toString());
    if (params.pageSize !== undefined)
      httpParams = httpParams.set('pageSize', params.pageSize.toString());
    add('sortBy', params.sortBy);
    add('sortOrder', params.sortOrder);
    if (params.paymentCompleted !== undefined)
      httpParams = httpParams.set(
        'paymentCompleted',
        params.paymentCompleted.toString()
      );
    add('serviceId', params.serviceId);
    add('professionalId', params.professionalId);

    return this.http
      .get<GetEventsForReportsResponse>(
        `${this.baseUrl}/api/v1/reports/events`,
        {
          params: httpParams,
          context: new HttpContext().set(REQUIRE_AUTH, true),
        }
      )
      .pipe(catchError(this.handleHttpError));
  }

  /**
   * GET /api/v1/reports/events (sin paginación para exportación)
   * Obtiene TODOS los eventos que coincidan con los filtros para exportación
   */
  getAllEventsForReports(
    params: Omit<GetEventsForReportsParams, 'page' | 'pageSize'> = {}
  ): Observable<GetEventsForReportsResponse> {
    // Usamos pageSize muy grande para obtener todos los resultados
    return this.getEventsForReports({
      ...params,
      page: 1,
      pageSize: 10000, // Número suficientemente grande
    });
  }

  private toIso(value?: string | Date): string | undefined {
    if (!value) return undefined;
    if (value instanceof Date) return value.toISOString();
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : d.toISOString();
  }
}
